
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Tabular
 *
 * @file Tabular.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Table, TableHead, TableBody, TableRow, TableCell,
  Typography, Paper,
  TablePagination, createTheme, ThemeProvider,
  TableContainer,
  IconButton,
  Checkbox,
  Button
} from '@material-ui/core';
// import Pagination from './components/Pagination';
import TabularStyles from './styles/TabularStyles';
import {
  OverviewTemplate,
  TextTemplate,
  BadgeTemplate,
  ProgressTemplate,
  TextChipTemplate,
  ContentTemplate,
  DateTemplate,
  ComprehensionTemplate,
  Info,
  DropdownTemplate
} from './components/Template';
import SearchBox from '../common/SearchBox';
import useAdvancedSearch from '../../hooks/useAdvancedSearch';
import Icon from '../icons/Icon';
import useSort from '../../hooks/useSort';
import useSelection from '../../hooks/useSelection';
import usePrevious from '../../hooks/usePervious';
import CustomScrollbar from '../CustomScrollbar/CustomScrollbar';
import ToolTipWrapper from '../tooltip/TooltipWrapper';
import Utils from '../../common/utils';
import CheckboxOutlineIcon from '../icons/CheckboxOutlineIcon';
import CheckboxCheckedIcon from '../icons/CheckboxCheckedIcon';
import TableFilter from '../common/TableFilter';
import useTableFilter from '../../hooks/useTableFilter';

const Tabular = (props) => {
  const {
    title,
    textInfo,
    columns,
    data,
    config,
    callback,
    onSelect,
    onRowClick,
    selectionDisableName = 'isStarted',
    onInit = () => null,
    isRowDisabled = () => false
  } = props;

  useEffect(() => {
    onInit();
  }, []);

  const {
    showPagination = false,
    showSearch = false,
    showTitle = false,
    showCheckBox = false,
    showColumnDivider = false,
    searchPlaceholder,
    showSelectionSummary,
    showButton,
    filterByText,
    showFilter,
    tableClassName,
    filterConfig,
    dropButtonLabel
  } = config || {};

  const {
    handleDropButtonClick,
    onHandleSearch,
    onHandleFilter
  } = callback || {};

  const classes = TabularStyles(props);
  const [page, setPage] = useState(0);
  const previousConfig = usePrevious(config);
  const { record,
    allItemsChecked,
    someItemsChecked,
    onSelectAndUnSelectItem,
    handleSelectAndUnSelectAll,
    resetItems,
    allItemsDisabled } = useSelection(data, onSelect, selectionDisableName);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const { search, setSearch } = useAdvancedSearch(record, { columns });
  const rowsPerPage = data.length;

  const filteredData = useTableFilter({
    data: record,
    search,
    columns,
    filterConfig,
    selectedFilter
  });
  const selectedRows = filteredData.filter(i => i.checked);
  const selectedCount = selectedRows.length;
  const totalCount = filteredData.filter(
    item => !item[selectionDisableName]
  ).length;
  const isDropButtonDisabled = selectedCount === 0;

  // Ref and state for reading CSS variable
  const containerRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(500);

  // Read CSS variable and convert to number
  useEffect(() => {
    if (containerRef.current) {
      const computedStyle = getComputedStyle(containerRef.current);
      const cssVarValue = computedStyle.getPropertyValue('--table-container-max-height').trim();

      if (cssVarValue) {
        // Remove 'px' and convert to number
        const heightValue = parseInt(cssVarValue.replace('px', ''), 10);
        if (!Number.isNaN(heightValue)) {
          setMaxHeight(heightValue);
        }
      }
    }
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const theme = createTheme({
    overrides: {
      MuiTableCell: {
        root: {
          padding: 8
        }
      },
      MuiTableRow: {
        root: {
          '&:hover': {
            cursor: 'pointer',
            backgroundColor: 'rgba(13, 0, 77, 0.15)'
          }
        }
      }
    }
  });

  useEffect(() => {
    setPage(0);
  }, [search]);

  const { sortedData, sortConfig, setSortConfig } = useSort(filteredData);

  const handleRowClick = (event, item) => {
    event.preventDefault();

    onRowClick(item);
  };
  const renderCell = (column, item) => {
    let renderData = null;
    try {
      renderData = column.render(item);
    // eslint-disable-next-line no-unused-vars
    } catch (error) {
      renderData = null;
    }

    return renderData;
  };

  useEffect(() => {
    if (JSON.stringify(previousConfig) !== JSON.stringify(config)) {
      resetItems();
    }
  }, [config, previousConfig, resetItems]);

  return (
    <ThemeProvider theme={theme}>
      <Paper
        ref={containerRef}
        className={classes.root}
        elevation={0}
      >
        <div className={classes.titleContainer}>
          {showTitle && (
            <div className={classes.titleContainerLeft}>
              <Typography variant="h6" gutterBottom>
                {title}
              </Typography>
            </div>
          )}
          {showSearch && (
            <div className={classes.titleContainerRight}>
              <SearchBox
                value={search}
                onChange={(value) => {
                  setSearch(value);
                  onHandleSearch?.(value);
                }}
                placeholder={searchPlaceholder}
              />
            </div>
          )}
          {showFilter && filterConfig && (
          <TableFilter
            classes={classes}
            filterByText={filterByText}
            filterConfig={filterConfig}
            value={selectedFilter}
            onChange={(value) => {
              setSelectedFilter(value);
              setPage(0);
              onHandleFilter?.(value);
            }}
          />
          )}
        </div>
        <TableContainer classes={{ root: classes.tableWrapper }}>
          <CustomScrollbar
            autoHeight
            autoHeightMax={maxHeight}
          >
            <Table stickyHeader className={`${classes.table} ${tableClassName || ''}`}>
              <TableHead>
                <TableRow classes={{ root: classes.tableRowRoot }}>
                  {columns.map((column, index) => {
                    const sanitizedTitle = Utils.sanitizeString(column?.title);

                    return (
                      <TableCell
                        key={column.title}
                        className={`${classes.tableCell} ${classes.tableThCell} ${!column?.sort ? classes.cursor : ''} ${column.cellClassName || ''}`}
                        classes={{ stickyHeader: classes.stickyHeader }}
                      >
                        <div id={`select-${sanitizedTitle}`} className={classes.tableCellContent} style={{ alignItems: 'center' }}>
                          {showCheckBox && index === 0 && (
                            <ToolTipWrapper
                              tooltip={textInfo.tooltip.allItemsSelectionDisabled}
                              showTooltip={allItemsDisabled}
                              placement="top"
                            >
                              <span>
                                <Checkbox
                                  classes={{ root: classes.paddingCheckbox }}
                                  indeterminate={!allItemsChecked && someItemsChecked}
                                  checked={allItemsChecked}
                                  style={{ paddingRight: 8 }}
                                  onChange={handleSelectAndUnSelectAll}
                                  disabled={allItemsDisabled}
                                  inputProps={{
                                    'aria-labelledby': `select-${sanitizedTitle}`
                                  }}
                                  icon={<CheckboxOutlineIcon className={classes.checkboxIcon} />}
                                  checkedIcon={<CheckboxCheckedIcon />}
                                />
                              </span>
                            </ToolTipWrapper>
                          )}
                          {column.title}

                          <Info {...(column?.info || {})} classes={{ infoIcon: classes.infoHeaderIcon }} />

                          {column.isSortable && column.sort && (
                            <IconButton
                              size="small"
                              className={classes.sortIcon}
                              onClick={() => {
                                const [field, type] = column.sort;
                                const direction =
                                  // eslint-disable-next-line no-nested-ternary
                                  sortConfig?.field === field
                                    ? sortConfig.direction === 'asc'
                                      ? 'desc'
                                      : 'asc'
                                    : 'desc';
                                setSortConfig({ field, type, direction });
                              }}
                            >
                              <Icon name="SortIcon" />
                            </IconButton>
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((item, idx) => {
                  const rowDisabled = isRowDisabled(item);

                  return (
                    <TableRow
                      key={idx}
                      classes={{ root: classes.tableBodyRowRoot }}
                      className={`
    ${rowDisabled ? classes.disabledRow : {}}
    ${item.hasError || item.requiresManualDateEntry || item.hasNoChaptersError
                        ? 'errorRow'
                        : ''
    }
  `}
                      onClick={(event) => {
                        if (rowDisabled) return;
                        handleRowClick(event, item);
                      }}
                    >
                      {columns.map((column, index) => (
                        <TableCell
                          key={column.title + index}
                          className={`${classes.tableCell} ${!showColumnDivider ? classes.tableBorder : ''} ${column.cellClassName || ''}`}
                        >
                          <div className={classes.tableCellContent}>
                            {showCheckBox && index === 0 && (
                            <ToolTipWrapper
                              tooltip={textInfo.tooltip.selectionDisabled}
                              showTooltip={item[selectionDisableName] || false}
                              placement="top"
                            >
                              <span>
                                <Checkbox
                                  classes={{ root: classes.paddingCheckbox }}
                                  checked={item.checked}
                                  disabled={item[selectionDisableName] || false}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                  }}
                                  onChange={(event) => {
                                    onSelectAndUnSelectItem(
                                      item.id,
                                      event.target.checked
                                    );
                                  }}
                                  inputProps={{
                                    'aria-labelledby': `select-${Utils.sanitizeString(item?.title)}`
                                  }}
                                  icon={<CheckboxOutlineIcon className={classes.checkboxIcon} />}
                                  checkedIcon={<CheckboxCheckedIcon />}
                                />
                              </span>
                            </ToolTipWrapper>
                            )}
                            {renderCell(column, item)}
                          </div>
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CustomScrollbar>
        </TableContainer>
        {showPagination && (
          <TablePagination
            // ActionsComponent={Pagination}
            rowsPerPageOptions={[rowsPerPage]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
          />
        )}
      </Paper>
      {showCheckBox && showSelectionSummary && totalCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 40 }}>

          <Typography variant="body2" className={classes.selectionSummary}>
            <span className={classes.selectionCount}>
              {selectedCount} of {totalCount}
            </span>
            <span className={classes.selectionLabel}> selected</span>
          </Typography>

          {showButton && (
          <Button
            onClick={() => handleDropButtonClick?.(selectedRows)}
            className={classes.dropButton}
            disabled={isDropButtonDisabled}
          >
            {dropButtonLabel}
          </Button>
          )}
        </div>
      )}
    </ThemeProvider>
  );
};

Tabular.propTypes = {
  title: PropTypes.string,
  textInfo: PropTypes.shape({
    tooltip: PropTypes.shape({
      selectionDisabled: PropTypes.string,
      allItemsSelectionDisabled: PropTypes.string
    })
  }),
  columns: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    render: PropTypes.func.isRequired,
    primarySearch: PropTypes.bool,
    primarySearchFields: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string
    ]),
    secondarySearch: PropTypes.bool,
    secondarySearchFields: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string
    ]),
    field: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    isSortable: PropTypes.bool,
    sort: PropTypes.array, // [field, type]
    cellClassName: PropTypes.string
  })),
  data: PropTypes.array,
  config: PropTypes.shape({
    showPagination: PropTypes.bool,
    showSearch: PropTypes.bool,
    showTitle: PropTypes.bool,
    showCheckBox: PropTypes.bool,
    showColumnDivider: PropTypes.bool,
    searchPlaceholder: PropTypes.string,
    searchLabel: PropTypes.string,
    showSelectionSummary: PropTypes.bool,
    showButton: PropTypes.bool,
    filterByText: PropTypes.string,
    inviteStudentsText: PropTypes.string,
    tableClassName: PropTypes.string

  }),
  callback: PropTypes.shape({
    handleDropButtonClick: PropTypes.func,
    onHandleSearch: PropTypes.func,
    onHandleFilter: PropTypes.func
  }),
  onSelect: PropTypes.func,
  onRowClick: PropTypes.func,
  selectionDisableName: PropTypes.string,
  onInit: PropTypes.func,
  isRowDisabled: PropTypes.func
};

Tabular.defaultProps = {
  title: '',
  textInfo: {
    tooltip: {
      selectionDisabled: 'Cannot delete: student work in progress',
      allItemsSelectionDisabled: 'Cannot delete: student work in progress'
    }
  },
  columns: [],
  data: [],
  config: {},
  callback: {},
  onSelect: () => {},
  onRowClick: () => {},
  selectionDisableName: 'isStarted',
  onInit: () => {},
  isRowDisabled: () => {}
};

export default Tabular;
export {
  OverviewTemplate,
  TextTemplate,
  BadgeTemplate,
  ProgressTemplate,
  TextChipTemplate,
  ContentTemplate,
  DateTemplate,
  ComprehensionTemplate,
  Info,
  DropdownTemplate
};
